import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { useApi } from "@/lib/api";

/**
 * Guides tab for /admin/creators/:id. Lists published and draft guides
 * that reference this creator's samoony_handle. Read-only; editing happens
 * on /admin/guides.
 */
export default function CreatorGuidesTab({ creator }) {
    const { request } = useApi();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!creator?.samoony_handle) {
            setItems([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await request("/admin/guides");
            const sh = creator.samoony_handle.toLowerCase();
            const hits = (res?.items || []).filter((g) =>
                (g.entries || []).some(
                    (e) =>
                        (e.samoony_handle || "").toLowerCase() === sh ||
                        (e.creator_samoony_handle || "").toLowerCase() === sh,
                ),
            );
            setItems(hits);
        } catch (err) {
            toast.error(err?.message || "Could not load guides.");
        } finally {
            setLoading(false);
        }
    }, [creator?.samoony_handle, request]);

    useEffect(() => {
        void load();
    }, [load]);

    if (loading) {
        return <div className="text-sm text-black/50">Loading guides...</div>;
    }

    if (!items.length) {
        return (
            <div
                data-testid="creator-guides-empty"
                className="bg-white border border-black/10 rounded-2xl p-5"
            >
                <div className="samoony-label mb-2">Guides</div>
                <p className="text-sm text-black/60">
                    Not listed in any guide yet. Suggest where this creator
                    fits on the Guides page.
                </p>
                <Link to="/admin/guides" className="samoony-chip mt-3 inline-flex">
                    Open guides
                </Link>
            </div>
        );
    }

    return (
        <section
            data-testid="creator-guides-list"
            className="bg-white border border-black/10 rounded-2xl"
        >
            <div className="px-5 py-3 border-b border-black/5 samoony-label">
                Guides ({items.length})
            </div>
            <ul className="divide-y divide-black/5">
                {items.map((g) => {
                    const sh = creator.samoony_handle.toLowerCase();
                    const entry = (g.entries || []).find(
                        (e) =>
                            (e.samoony_handle || "").toLowerCase() === sh ||
                            (e.creator_samoony_handle || "").toLowerCase() === sh,
                    );
                    return (
                        <li
                            key={g.id}
                            data-testid={`creator-guide-row-${g.slug}`}
                            className="px-5 py-3 flex flex-wrap items-start justify-between gap-3"
                        >
                            <div className="min-w-0">
                                <div className="text-sm flex items-center gap-2 flex-wrap">
                                    <strong className="font-medium">
                                        {g.title}
                                    </strong>
                                    <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${g.status === "published" ? "bg-signal/15 text-signal" : "bg-black/5 text-black/60"}`}
                                    >
                                        {g.status}
                                    </span>
                                    <span className="font-mono text-[10px] uppercase tracking-wider text-black/40">
                                        {g.guide_type === "topic"
                                            ? "Topic"
                                            : "Creators"}
                                    </span>
                                </div>
                                {entry?.line && (
                                    <div className="text-xs text-black/50 mt-1">
                                        "{entry.line}"
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {g.status === "published" && (
                                    <a
                                        href={`https://samoony.com/guides/${g.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-[11px] underline text-black/50 hover:text-ink"
                                    >
                                        Live ↗
                                    </a>
                                )}
                                <Link to="/admin/guides" className="samoony-chip">
                                    Edit
                                </Link>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
